import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import SiteTable from "./site-table";
import SiteCard from "./site-card/site-card";
import ApiSiteManagement from "../../../api/site-managements";
import useNotification from "../../../components/notification/notification";
import "./styles.css";
import { Body } from "../../../components/main-body/body";
import { Modal } from "antd";
import { useParams, useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import debounce from "../../../common/utils/debounce";
import useAdminEvents from "../../../hooks/useAdminEvents";

const DeleteModalComponent = ({ title, handleOk, handleCancel, isModalOpen }) => {
  return (
    <Modal
      title={title}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{ danger: true }}
      okText="Delete"
      className="plan-page-action-modal"
    >
      <p>Are you sure you want to delete this site?</p>
    </Modal>
  );
};

const SORT_FIELDS = {
  name: "name",
  subjects: "subjectCount",
  status: "status",
  updatedAt: "updatedAt",
};

const SiteManagement = ({ mode }) => {
  const [sites, setSites] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSite, setSelectedSite] = useState({});
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const [isOpenDeleteSiteModal, setOpenDeleteSiteModal] = useState();
  const { id } = useParams();
  const navigate = useProjectNavigate();
  const location = useLocation();

  const [sort, setSort] = useState({ field: SORT_FIELDS.name, order: "ASC" });
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});

  // guards / refs проти повторних фетчів
  const didInitRef = useRef(false);
  const inflightRef = useRef(false);
  const lastQueryRef = useRef({ page: null, key: "" });

  const handleNavigate = (path) => {
    if (location.pathname !== path) navigate(path);
  };

  const baseParams = useMemo(() => {
    const selectedStatuses = Array.isArray(filters?.status) ? filters.status : undefined;
    return {
      sortBy: sort.field,
      sortOrder: sort.order,
      ...(selectedStatuses && selectedStatuses.length ? { status: selectedStatuses } : {}),
      ...(search && search.trim() ? { search: search.trim() } : {}),
    };
  }, [sort, filters, search]);

  const getSites = useCallback(
    async ({ append = false, pageParam } = {}) => {
      const currentPage = pageParam ?? page;

      const key = JSON.stringify(baseParams);
      if (lastQueryRef.current.page === currentPage && lastQueryRef.current.key === key) return;
      if (inflightRef.current) return;

      inflightRef.current = true;
      setLoading(true);
      try {
        const { data } = await ApiSiteManagement.getSites({ ...baseParams, page: currentPage });
        const pageItems = data?.sites || [];

        setSites((prev) => (append ? [...prev, ...pageItems] : pageItems));
        setHasMore(!!data?.hasMore);

        if (Array.isArray(data?.users)) setUsers(data.users);
        if (Array.isArray(data?.subjects)) setSubjects(data.subjects);

        lastQueryRef.current = { page: currentPage, key };
      } finally {
        setLoading(false);
        inflightRef.current = false;
      }
    },
    [baseParams, page]
  );

  const refreshSites = useCallback(() => {
    lastQueryRef.current = { page: null, key: "" };
    if (page !== 1) {
      setPage(1);
      return Promise.resolve();
    }
    return getSites({ append: false, pageParam: 1 });
  }, [getSites, page]);

  const debouncedRefreshFirstPage = useMemo(
    () =>
      debounce(async () => {
        if (!didInitRef.current) return;
        if (page !== 1) {
          setPage(1);
        } else {
          await getSites({ append: false, pageParam: 1 });
        }
      }, 400),
    [getSites, page]
  );

  const headerProps = useMemo(
    () => ({
      title: "Sites",
      search: {
        value: search,
        onHandleChange: setSearch,
      },
      create: {
        testID: "site-create",
        label: t("site_management.create"),
        onClick: () => openSiteDrawer(true),
      },
    }),
    [isOpen, t, search]
  );

  useEffect(() => {
    getSites({ append: false, pageParam: 1 }).finally(() => {
      didInitRef.current = true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!didInitRef.current) return;
    if (page !== 1) setPage(1);
    else getSites({ append: false, pageParam: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort.field, sort.order]);

  useEffect(() => {
    if (!didInitRef.current) return;
    debouncedRefreshFirstPage();
    return () => debouncedRefreshFirstPage.cancel?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, search]);

  useEffect(() => {
    if (!didInitRef.current) return;
    if (page > 1) getSites({ append: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (!id && isOpen) {
      setOpen(false);
      setSelectedSite({});
    }
  }, [id, isOpen]);

  useEffect(() => {
    if (id && sites.length) {
      const site = sites.find((site) => site.id === id);
      if (site) {
        setSelectedSite(site);
        setOpen(true);
      }
    } else {
      setSelectedSite({});
    }
  }, [id, sites]);

  useEffect(() => {
    setEditMode(mode === "edit" || mode === "create");
    if (mode === "create") {
      setOpen(true);
      setSelectedSite({});
    }
  }, [mode]);

  useAdminEvents({
    onSiteChanged: (payload) => {
      refreshSites();

      const changedIds = [
        ...(payload?.deletedIds || []),
        payload?.id,
      ].filter(Boolean);

      setSelectedSite((prev) => {
        if (!prev?.id || !changedIds.includes(prev.id)) return prev;

        if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
          handleNavigate("/administration/sites");
          setOpen(false);
          setEditMode(false);
          return {};
        }

        return payload?.site ? { ...prev, ...payload.site } : prev;
      });
    },
  });

  const openSiteDrawer = (edit) => {
    if (edit) setEditMode(true);

    if (selectedSite?.id) {
      if (edit) handleNavigate(`/administration/sites/${selectedSite.id}/edit`);
      else handleNavigate(`/administration/sites/${selectedSite.id}`);
    } else {
      handleNavigate(`/administration/sites/create`);
    }
    setOpen(true);
  };

  const cancelEditing = () => {
    handleNavigate(`/administration/sites/${selectedSite.id}`);
    setEditMode(false);
  };
  const closeSiteDrawer = () => {
    handleNavigate(`/administration/sites`);
    setEditMode(false);
    setOpen(false);
    setSelectedSite({});
  };

  const createSite = (site) => {
    setEditMode(false);
    ApiSiteManagement.createSite(site)
      .then((response) => {
        const createdSiteId = response.data.site.id;
        lastQueryRef.current = { page: null, key: "" };
        getSites({ append: false, pageParam: 1 }).then(() => {
          openNotification({
            type: "success",
            message: <p>{t("site_management.notification.created", { site: site.name })}</p>,
          });
          handleNavigate(`/administration/sites/${createdSiteId}`);
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: <p>{t(`site_management.notification.${e?.response?.data?.error?.code || "error"}`)}</p>,
        });
      });
  };

  const toggleActive = async (item) => {
    try {
      await ApiSiteManagement.toggleStatus(item.id);
      openNotification?.({ type: "success", message: <p>Status changed</p> });
      lastQueryRef.current = { page: null, key: "" };
      await getSites({ append: false, pageParam: 1 });
    } catch (err) {
      openNotification?.({ type: "error", message: <p>Failed to change status</p> });
    }
  };

  const updateSite = (id, site) => {
    setEditMode(false);
    handleNavigate(`/administration/sites/${site.id}`);

    ApiSiteManagement.updateSite(id, site)
      .then(() => {
        lastQueryRef.current = { page: null, key: "" };
        getSites({ append: false, pageParam: 1 }).then(() => {
          openNotification({
            type: "success",
            message: <p>{t("site_management.notification.updated", { site: site.name })}</p>,
          });
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: <p>{t(`site_management.notification.${e?.response?.data?.error?.code || "error"}`)}</p>,
        });
      });
  };

  const deleteSite = (id, name) => {
    closeSiteDrawer();
    ApiSiteManagement.deleteSite(id)
      .then(() => {
        lastQueryRef.current = { page: null, key: "" };
        return getSites({ append: false, pageParam: 1 });
      })
      .then(() => {
        openNotification({
          type: "success",
          message: <p>{t("site_management.notification.deleted", { site: name })}</p>,
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: <p>{t(`site_management.notification.${e?.response?.data?.error?.code || "error"}`)}</p>,
        });
      });
  };

  const handleSelectSite = (site) => {
    handleNavigate(`/administration/sites/${site.id}`);
    setSelectedSite(site);
    setOpen(true);
  };

  const toEditMode = () => {
    handleNavigate(`/administration/sites/${selectedSite.id}/edit`);
    setEditMode(true);
  };

  const openDeleteModal = () => setOpenDeleteSiteModal(true);
  const closeDeleteModal = () => setOpenDeleteSiteModal(false);
  const handleDeleteOk = () => {
    deleteSite(selectedSite.id, selectedSite.name);
    closeDeleteModal();
  };
  const handleDeleteCancel = () => closeDeleteModal();

  const handleTableChange = (_pagination, _filters, sorter) => {
    const fieldKey = sorter?.columnKey || sorter?.field;
    if (!fieldKey || !sorter?.order) return;

    const mappedField =
      fieldKey === "subjects" ? SORT_FIELDS.subjects : SORT_FIELDS[fieldKey] || fieldKey;

    const next = {
      field: mappedField,
      order: sorter.order === "ascend" ? "ASC" : "DESC",
    };
    setSort((prev) => (prev.field === next.field && prev.order === next.order ? prev : next));
  };

  const statusOptions = [
    { id: "active", name: t("common.active") || "Active" },
    { id: "inactive", name: t("common.inactive") || "Inactive" },
  ];

  return (
    <Body
      header={headerProps}
      filters={
        <DropdownFilters
          filtersBy={[
            { name: t("task_management.table.status"), id: "status", children: statusOptions },
          ]}
          updateFilterValue={setFilters}
        />
      }
    >
      <SiteTable
        data={sites}
        sort={sort}
        onTableChange={handleTableChange}
        onHandleRowClick={handleSelectSite}
        isLoading={loading}
        hasMore={hasMore}
        fetchData={() => {
          if (!loading && hasMore) setPage((p) => p + 1);
        }}
      />
      <SiteCard
        site={selectedSite}
        subjects={subjects}
        users={users}
        isOpen={isOpen}
        close={closeSiteDrawer}
        editMode={editMode}
        toEditMode={toEditMode}
        createSite={createSite}
        updateSite={updateSite}
        deleteSite={openDeleteModal}
        cancelEditing={cancelEditing}
        toggleActive={toggleActive}
      />
      <DeleteModalComponent
        isModalOpen={isOpenDeleteSiteModal}
        handleOk={handleDeleteOk}
        handleCancel={handleDeleteCancel}
        title={selectedSite.name}
      />
    </Body>
  );
};

export default SiteManagement;
